/**
 * /methodology — static page explaining how Veridex calculates PCF and risk.
 * Kept deliberately plain-English so CFOs and non-technical stakeholders can
 * read it without a sustainability background.
 */
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  BookOpen, ShieldCheck, Scale, FileCheck2, Info, ArrowLeft,
} from 'lucide-react';

export default function Methodology() {
  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="methodology-page">
      <div>
        <Link to="/simple-dashboard" className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1 mb-3" data-testid="methodology-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to dashboard
        </Link>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-3">
          <BookOpen className="w-3.5 h-3.5" /> Methodology
        </div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">
          How Veridex calculates your carbon footprint
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          A plain-English walkthrough of the formulas, data sources and safeguards behind every number on your dashboard.
        </p>
      </div>

      <Card className="p-6 sm:p-8" data-testid="methodology-section-pcf">
        <div className="flex items-center gap-2 mb-3">
          <Scale className="w-5 h-5 text-primary" />
          <h2 className="text-xl font-heading font-semibold">1 · What is a Product Carbon Footprint (PCF)?</h2>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          A PCF is the total greenhouse-gas emissions associated with producing one unit of
          a product, expressed in <strong>kilograms of CO₂-equivalent per unit</strong>
          (kg CO₂e / unit). We align with the <strong>GHG Protocol Product Standard</strong>
          and <strong>ISO 14067</strong>, using a cradle-to-gate boundary (raw material
          extraction through to the factory gate).
        </p>
        <div className="mt-4 p-4 rounded-lg bg-muted/40 border border-border font-mono text-xs">
          PCF = (Scope 1 direct emissions)
          <br />&nbsp;&nbsp;&nbsp;&nbsp;+ (Scope 2 purchased electricity)
          <br />&nbsp;&nbsp;&nbsp;&nbsp;+ (Scope 3 upstream raw materials &amp; supplier data)
          <br />&nbsp;&nbsp;÷ production volume (units)
        </div>
      </Card>

      <Card className="p-6 sm:p-8" data-testid="methodology-section-risk">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <h2 className="text-xl font-heading font-semibold">2 · How we assign your Risk Label</h2>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          The Low / Medium / High label on your Simple dashboard is a fast comparison to
          typical benchmarks for your sector. It helps you triage which products to act on
          first — it is not a regulatory verdict.
        </p>
        <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
          <div className="p-3 rounded-lg border border-green-300 bg-green-50 dark:bg-green-950/30">
            <div className="font-semibold text-green-800 dark:text-green-200">Low</div>
            <div className="text-xs mt-1 text-green-700 dark:text-green-300">PCF &lt; 1.5 kg CO₂e/unit</div>
          </div>
          <div className="p-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30">
            <div className="font-semibold text-amber-800 dark:text-amber-200">Medium</div>
            <div className="text-xs mt-1 text-amber-700 dark:text-amber-300">1.5 – 3.0 kg CO₂e/unit</div>
          </div>
          <div className="p-3 rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/30">
            <div className="font-semibold text-red-800 dark:text-red-200">High</div>
            <div className="text-xs mt-1 text-red-700 dark:text-red-300">&gt; 3.0 kg CO₂e/unit</div>
          </div>
        </div>
      </Card>

      <Card className="p-6 sm:p-8" data-testid="methodology-section-data">
        <div className="flex items-center gap-2 mb-3">
          <FileCheck2 className="w-5 h-5 text-primary" />
          <h2 className="text-xl font-heading font-semibold">3 · Data sources &amp; emission factors</h2>
        </div>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li>• <strong>Electricity:</strong> Ember Global Electricity Review 2024 (country-specific grid factors).</li>
          <li>• <strong>Fuels &amp; direct combustion:</strong> IPCC 2024 Guidelines for National GHG Inventories.</li>
          <li>• <strong>Materials:</strong> ecoinvent 3.10, peer-reviewed LCAs, and supplier-reported data where available.</li>
          <li>• <strong>Global Warming Potentials:</strong> IPCC AR6 100-year GWPs.</li>
          <li>• <strong>Freshness:</strong> every factor in the Compliance Report is flagged if older than 90 days.</li>
        </ul>
      </Card>

      <Card className="p-6 sm:p-8" data-testid="methodology-section-review">
        <div className="flex items-center gap-2 mb-3">
          <Info className="w-5 h-5 text-primary" />
          <h2 className="text-xl font-heading font-semibold">4 · Human review &amp; audit trail</h2>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          No report leaves the platform on autopilot. Every PCF record carries a
          <strong> review status</strong> (pending / approved / rejected). PDF exports,
          CBAM XML filings and compliance reports are locked until a qualified reviewer
          approves the numbers. Every approval, edit and data-source change is persisted
          in our audit trail, visible at{' '}
          <Link to="/transparency" className="text-primary hover:underline">/transparency</Link>.
        </p>
      </Card>

      <Card className="p-6 sm:p-8 bg-gradient-to-br from-primary/5 to-accent/5 border-primary/30" data-testid="methodology-cta">
        <h2 className="text-xl font-heading font-semibold mb-2">Want the full technical spec?</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Our detailed whitepaper (system boundary, allocation rules, uncertainty quantification and sample calculations) is available on request to paying customers and auditors.
        </p>
        <Link to="/contact">
          <Button className="bg-primary hover:bg-primary/90" data-testid="methodology-contact-btn">Request the whitepaper</Button>
        </Link>
      </Card>

      <p className="text-xs text-muted-foreground text-center pt-4">
        This methodology is a readiness framework, not a regulatory certification. For audit sign-off, engage a qualified third-party verifier.
      </p>
    </div>
  );
}
